import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { partnerAffiliations } from '@/mocks/aboutData';

export default function About() {
  const { t } = useTranslation();

  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-12 md:py-16 bg-primary-900">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50">{t('about.title')}</h1>
          <p className="text-sm md:text-base text-background-50/70 mt-4 max-w-2xl mx-auto">{t('about.subtitle')}</p>
        </div>
      </section>

      {/* History */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-14">
            {/* Image */}
            <div className="w-full lg:w-2/5 flex-shrink-0">
              <div className="relative">
                <div className="w-64 h-64 md:w-72 md:h-72 mx-auto lg:mx-0 rounded-2xl overflow-hidden border-2 border-accent-500/30">
                  <img
                    src="https://static.readdy.ai/image/dfcab6d8a996275ea038131e02fb9f3a/c7bdcb3ed87590029d6e0640a43364dd.jpeg"
                    alt="Baby Olivier Pandja — Président du Tennis Club de Douala"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-3 -right-3 w-24 h-24 border-2 border-accent-500/20 rounded-2xl hidden lg:block"></div>
                <div className="absolute -top-3 -left-3 w-16 h-16 border-2 border-accent-500/10 rounded-2xl hidden lg:block"></div>
              </div>
              <p className="text-center lg:text-left mt-5 font-heading text-xl text-foreground-950 font-semibold">Baby Olivier Pandja</p>
              <p className="text-center lg:text-left text-sm text-accent-600 tracking-wide uppercase">{t('vision.subtitle')}</p>
            </div>

            {/* Text */}
            <div className="flex-1 text-center lg:text-left">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">{t('about.history')}</h2>
              <div className="space-y-4">
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{t('about.historyText1')}</p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{t('about.historyText2')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* President Quote */}
      <section className="py-12 md:py-16 bg-primary-900">
        <div className="max-w-5xl mx-auto px-4 md:px-6 text-center">
          <blockquote className="font-heading text-xl md:text-2xl italic text-background-50/90 max-w-3xl mx-auto leading-relaxed">
            Notre ambition est de faire du tennis club de Douala le lieu de référence du tennis à Douala et au Cameroun. Nous pratiquons le tennis et assurons sa promotion à travers la détection des futurs champions.
          </blockquote>
          <p className="text-accent-400 font-semibold mt-4">Baby Olivier Pandja</p>
          <p className="text-background-50/50 text-sm">Président du Tennis Club de Douala</p>
        </div>
      </section>

      {/* Values */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{t('about.values')}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { key: 'Excellence', descKey: 'about.valuesExcellenceDesc', icon: 'ri-star-line' },
              { key: 'Formation', descKey: 'about.valuesTrainingDesc', icon: 'ri-graduation-cap-line' },
              { key: 'Communauté', descKey: 'about.valuesCommunityDesc', icon: 'ri-heart-line' },
              { key: 'Innovation', descKey: 'about.valuesInnovationDesc', icon: 'ri-lightbulb-line' },
            ].map((val) => (
              <div key={val.key} className="bg-background-50 rounded-2xl p-5 border border-background-200/70 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 mx-auto mb-3">
                  <i className={`${val.icon} text-lg text-accent-600`}></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">{t(`about.values${val.key}`)}</h3>
                <p className="text-xs text-foreground-500">{t(val.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{t('about.partners')}</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {partnerAffiliations.map((partner, idx) => (
              <div key={idx} className="bg-background-100 rounded-xl p-4 text-center border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 mx-auto mb-2">
                  <i className={`${partner.icon} text-accent-600`}></i>
                </div>
                <p className="text-xs text-foreground-600 font-medium">{partner.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16 bg-accent-500">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">{t('about.contactCta')}</h2>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50 text-foreground-900 font-semibold text-sm rounded-full hover:bg-background-100 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            {t('about.contactBtn')}
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-xs"></i>
            </div>
          </Link>
        </div>
      </section>

      <Footer />
      <div className="fixed bottom-6 right-6 z-40 flex flex-col gap-3">
        <a
          href="tel:+237687696969"
          className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-accent-500 text-white hover:bg-accent-600 transition-all duration-300 hover:scale-110 cursor-pointer shadow-lg"
          aria-label="Appeler"
        >
          <i className="ri-phone-fill text-2xl md:text-3xl"></i>
        </a>
        <a href="https://wa.me/237687696969" target="_blank" rel="noopener noreferrer" className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-[#25D366] text-white hover:bg-[#22c55e] transition-all duration-300 hover:scale-110 cursor-pointer" aria-label="Contact WhatsApp">
          <i className="ri-whatsapp-line text-2xl md:text-3xl"></i>
        </a>
      </div>
    </main>
  );
}